import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Plus, Filter, BarChart3, Settings } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import ProductManagementModal from '../components/ProductManagementModal';
import CategoryManagementModal from '../components/CategoryManagementModal';
import BrandManagementModal from '../components/BrandManagementModal';
import SupplierManagementModal from '../components/SupplierManagementModal';
import InventoryStatsModal from '../components/InventoryStatsModal';

const CatalogManagement: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { t } = useI18n();
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.02,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
  };

  const managementCards = [
    {
      title: t('products'),
      description: t('productsDescription'),
      icon: Package,
      color: 'bg-blue-500',
      onClick: () => {
        console.log('🔄 Abriendo modal de Gestión de Productos');
        setShowProductModal(true);
      },
    },
    {
      title: t('categories'),
      description: t('categoriesDescription'),
      icon: Filter,
      color: 'bg-green-500',
      onClick: () => {
        console.log('🔄 Abriendo modal de Gestión de Categorías');
        setShowCategoryModal(true);
      },
    },
    {
      title: t('brands'),
      description: t('brandsDescription'),
      icon: Settings,
      color: 'bg-purple-500',
      onClick: () => {
        console.log('🔄 Abriendo modal de Gestión de Marcas');
        setShowBrandModal(true);
      },
    },
    {
      title: t('suppliers'),
      description: t('suppliersDescription'),
      icon: Plus,
      color: 'bg-orange-500',
      onClick: () => {
        console.log('🔄 Abriendo modal de Gestión de Proveedores');
        setShowSupplierModal(true);
      },
    },
    {
      title: t('statistics'),
      description: t('statisticsDescription'),
      icon: BarChart3,
      color: 'bg-red-500',
      onClick: () => {
        console.log('🔄 Abriendo modal de Estadísticas');
        setShowStatsModal(true);
      },
    },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-slate-900' : 'bg-gray-50'
    }`}>
      <div className="container mx-auto px-4 pt-20 pb-24">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {t('catalogManagement')}
            </h1>
            <p className={`text-lg ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t('catalogManagementDescription')}
            </p>
            {user && (
              <p className={`text-sm mt-2 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {t('connectedAs')}: {user.email}
              </p>
            )}
          </motion.div>

          {/* Management Cards Grid */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
          >
            {managementCards.map((card, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover="hover"
                onClick={card.onClick}
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-200 ${
                  isDark 
                    ? 'bg-slate-800 hover:bg-slate-700 border border-slate-700' 
                    : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-lg hover:shadow-xl'
                }`}
              >
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-xl ${card.color} text-white mr-4`}>
                    <card.icon size={24} />
                  </div>
                  <h3 className={`text-xl font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {card.title}
                  </h3>
                </div>
                <p className={`text-sm ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {card.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            variants={itemVariants}
            className={`p-6 rounded-2xl ${
              isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
            }`}
          >
            <h3 className={`text-xl font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {t('quickActions')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setShowProductModal(true)}
                className={`p-4 rounded-xl text-left transition-colors duration-200 ${
                  isDark 
                    ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                <Plus size={20} className="mb-2" />
                <div className="font-medium">{t('addProduct')}</div>
                <div className="text-sm opacity-75">{t('addProductDescription')}</div>
              </button>
              
              <button
                onClick={() => setShowCategoryModal(true)}
                className={`p-4 rounded-xl text-left transition-colors duration-200 ${
                  isDark 
                    ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                <Filter size={20} className="mb-2" />
                <div className="font-medium">{t('configureCategory')}</div>
                <div className="text-sm opacity-75">{t('configureCategoryDescription')}</div>
              </button>
              
              <button
                onClick={() => setShowStatsModal(true)}
                className={`p-4 rounded-xl text-left transition-colors duration-200 ${
                  isDark 
                    ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                <BarChart3 size={20} className="mb-2" />
                <div className="font-medium">{t('viewStatistics')}</div>
                <div className="text-sm opacity-75">{t('viewStatisticsDescription')}</div>
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Modals */}
      <ProductManagementModal 
        isOpen={showProductModal} 
        onClose={() => {
          console.log('❌ Cerrando modal de Gestión de Productos');
          setShowProductModal(false);
        }} 
      />
      
      <CategoryManagementModal 
        isOpen={showCategoryModal} 
        onClose={() => {
          console.log('❌ Cerrando modal de Gestión de Categorías');
          setShowCategoryModal(false);
        }} 
      />
      
      <BrandManagementModal 
        isOpen={showBrandModal} 
        onClose={() => {
          console.log('❌ Cerrando modal de Gestión de Marcas');
          setShowBrandModal(false);
        }} 
      />
      
      <SupplierManagementModal 
        isOpen={showSupplierModal} 
        onClose={() => {
          console.log('❌ Cerrando modal de Gestión de Proveedores');
          setShowSupplierModal(false);
        }} 
      />
      
      <InventoryStatsModal 
        isOpen={showStatsModal} 
        onClose={() => {
          console.log('❌ Cerrando modal de Estadísticas');
          setShowStatsModal(false);
        }} 
      />
    </div>
  );
};

export default CatalogManagement;
